/**
 * The universes the app generates itself, beside the index lists of
 * `lib/indices/slugs.ts` (README "One place for the universes"): every issue
 * an exchange lists, from Alpaca's asset list, refreshed each night by
 * `scripts/breadth-listings.ts`. They are stored in `index_membership` under
 * their own slugs, so reading, exporting and the nightly dated history
 * (`index_membership_history`) work as for any list, and they are kept out of
 * `INDEX_SLUGS`, whose lists the FMP refresh, the seed files and the
 * seasonality precompute walk.
 *
 * Measured 2026-10-26: Alpaca lists 2,895 active issues on the NYSE (the
 * NYSE's own figure was about 3,000 in 2020), 5,619 on Nasdaq, and 292 on
 * NYSE American (281 in 2020), and keeps NYSE Arca's 2,749 funds apart; FMP's
 * company file calls Arca's funds "AMEX" and cannot. A preferred is `BAC.PRB`
 * there (`BACpB` in the app), a right `TRAD.RT` (`TRAD.RI`). Pure: no
 * database, no fetch.
 */
import type { BreadthIssueUniverse } from "@/lib/signals/breadthSeries";

export type GeneratedExchange = "NYSE" | "NASDAQ" | "AMEX";

export type GeneratedList = {
  slug: "nyse_all" | "nasdaq_all" | "amex_all";
  exchange: GeneratedExchange;
  label: string;
  /** The breadth series that counts this list. */
  series: BreadthIssueUniverse;
};

export const GENERATED_LISTS: readonly GeneratedList[] = [
  { slug: "nyse_all", exchange: "NYSE", label: "NYSE All Issues", series: "NYA" },
  { slug: "nasdaq_all", exchange: "NASDAQ", label: "Nasdaq All Issues", series: "NQA" },
  { slug: "amex_all", exchange: "AMEX", label: "NYSE American All Issues", series: "AMA" },
];

export type GeneratedSlug = GeneratedList["slug"];

export function generatedListOf(raw: string): GeneratedList | null {
  const k = String(raw ?? "").trim().toLowerCase();
  return GENERATED_LISTS.find((g) => g.slug === k) ?? null;
}

/** The source stamp the lists carry in `index_uploads`. */
export const GENERATED_SOURCE = "alpaca";

/**
 * A ticker Alpaca lists as a security, not a CUSIP or an internal code it
 * keeps for a delisted issue (`09175M705`, `G2007L154`, `750RGT011`): letters,
 * with at most one dotted suffix.
 */
export function isListedTicker(alpacaSymbol: string): boolean {
  return /^[A-Z]{1,6}(\.[A-Z]{1,4})?$/.test(alpacaSymbol);
}

/**
 * Alpaca's ticker in the app's canonical form, the one the bars are stored
 * under (lib/providers/fmpSymbols.ts): a preferred series `BAC.PRB` is
 * `BACpB`, a right `TRAD.RT` is `TRAD.RI`; units (`.U`), warrants (`.WS`) and
 * classes (`BRK.B`) are written alike.
 */
export function alpacaToCanonical(alpacaSymbol: string): string {
  const s = alpacaSymbol.trim().toUpperCase();
  const pref = /^([A-Z]+)\.PR([A-Z]{1,2})$/.exec(s);
  if (pref) return `${pref[1]}p${pref[2]}`;
  if (s.endsWith(".RT")) return `${s.slice(0, -3)}.RI`;
  return s;
}

/** The canonical ticker in Alpaca's form, for its stream: the inverse of `alpacaToCanonical`. */
export function canonicalToAlpaca(symbol: string): string {
  const pref = /^([A-Z]+)p([A-Z]{1,2})$/.exec(symbol);
  if (pref) return `${pref[1]}.PR${pref[2]}`;
  if (symbol.endsWith(".RI")) return `${symbol.slice(0, -3)}.RT`;
  return symbol;
}

export type IssueKind = "common" | "preferred" | "etf" | "fund" | "unit" | "warrant" | "right";

/**
 * What an issue is, for the lists' kind column. Membership does not depend on
 * it: an all-issues list is every issue. The catalog's type wins where the
 * app has one (`ETF`); otherwise the ticker's form (the NYSE's suffixes, a
 * Nasdaq fifth letter with the name's word) and then the name.
 */
export function issueKindOf(alpacaSymbol: string, name: string | null, catalogType: string | null): IssueKind {
  const s = alpacaSymbol.toUpperCase();
  const n = name ?? "";
  if (catalogType === "ETF") return "etf";
  if (/\.PR[A-Z]{1,2}$/.test(s) || /\b(preferred|depositary shares?)\b/i.test(n)) return "preferred";
  if (s.endsWith(".U") || (s.length === 5 && s.endsWith("U") && /\bunits?\b/i.test(n))) return "unit";
  if (s.endsWith(".WS") || (s.length === 5 && s.endsWith("W") && /\bwarrants?\b/i.test(n))) return "warrant";
  if (s.endsWith(".RT") || (s.length === 5 && s.endsWith("R") && /\brights?\b/i.test(n))) return "right";
  if (/\b(ETF|ETN|exchange[- ]traded)\b/i.test(n)) return "etf";
  if (/\b(fund|trust)\b/i.test(n) && !/\b(inc|corp|corporation|company|co)\.?$/i.test(n.trim())) return "fund";
  return "common";
}
